import React, { useContext, useState, useEffect } from 'react'
import { X, Download } from 'lucide-react'
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer'
import ModalContext from '../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../contexts/OccurrenceContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import ApiService from '../../../services/ApiService'
import { Occurrence } from '../../../pages/ClientPanel/Occurrences/OccurencesTable/types'
import logoDustre from '../../../assets/Cert/logo-dustre-cert.png'
import {
    Container,
    Header,
    TitleRow,
    CloseButton,
    InfosContainer,
    Section,
    SectionDivider,
    SectionSubtitle,
    InfoGrid,
    InfoItem,
    InfoLabel,
    InfoValue,
    TextBlock,
    TextBlockLabel,
    SimpleTable,
    SimpleTableRow,
    TableHeader,
    TableCell,
    RelevantBadge,
    ButtonContainer,
    ButtonDiv,
} from './style'

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 28,
        paddingHorizontal: 28,
        paddingBottom: 24,
        fontSize: 10,
        color: '#1F1F1F',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#EAEAEA',
        paddingBottom: 10,
        marginBottom: 16,
    },
    logo: {
        width: 110,
        height: 40,
        objectFit: 'contain',
    },
    title: {
        fontSize: 16,
        fontWeight: 700,
    },
    subtitle: {
        fontSize: 11,
        color: '#595959',
        marginTop: 2,
    },
    section: {
        marginBottom: 12,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 700,
        marginBottom: 6,
    },
    row: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    label: {
        width: 120,
        fontWeight: 700,
        color: '#595959',
    },
    value: {
        flex: 1,
    },
    textBlock: {
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderRadius: 4,
        padding: 8,
        lineHeight: 1.4,
    },
    listItem: {
        marginBottom: 6,
        lineHeight: 1.4,
    },
})

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    } catch {
        return dateStr
    }
}

const OccurrencePDFDocument: React.FC<{
    data: Occurrence
    providences: Array<{ description: string; responsibleName: string | null; deadline: string }>
    files: Array<{ id: number; name: string }>
}> = ({ data, providences, files }) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
            <View style={pdfStyles.header}>
                <View>
                    <Image src={logoDustre} style={pdfStyles.logo} />
                    <Text style={pdfStyles.subtitle}>Aços e Ligas Especiais</Text>
                </View>
                <View>
                    <Text style={pdfStyles.title}>Ocorrência #{data.id}</Text>
                    <Text style={pdfStyles.subtitle}>Documento gerado automaticamente</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Informações gerais</Text>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Relevante:</Text>
                    <Text style={pdfStyles.value}>{data.relevant ? 'Sim' : 'Nao'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>OP:</Text>
                    <Text style={pdfStyles.value}>{data.operationNumber ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Cliente:</Text>
                    <Text style={pdfStyles.value}>{data.companyName || data.contactName || 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Setor:</Text>
                    <Text style={pdfStyles.value}>{data.sector ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Alocação:</Text>
                    <Text style={pdfStyles.value}>{data.allocation ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Etapa:</Text>
                    <Text style={pdfStyles.value}>{data.Stage?.name ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Quantidade:</Text>
                    <Text style={pdfStyles.value}>{String(data.quantity ?? 'N/A')}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Descrição</Text>
                <View style={pdfStyles.textBlock}>
                    <Text>{data.description || 'Sem descricao.'}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Causa e correção</Text>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Causa:</Text>
                    <Text style={pdfStyles.value}>{data.cause || 'Nao informada.'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Correção:</Text>
                    <Text style={pdfStyles.value}>{data.correction || 'Nao informada.'}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Providências</Text>
                {providences.length > 0 ? (
                    providences.map((providence, index) => (
                        <Text key={`${providence.description}-${index}`} style={pdfStyles.listItem}>
                            {`${index + 1}. ${providence.description || '-'} | Responsavel: ${providence.responsibleName || '-'} | Prazo: ${formatDate(providence.deadline)}`}
                        </Text>
                    ))
                ) : (
                    <Text>Nenhuma providência registrada.</Text>
                )}
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Anexos</Text>
                {files.length > 0 ? (
                    files.map((file, index) => (
                        <Text key={file.id} style={pdfStyles.listItem}>
                            {`${index + 1}. ${file.name}`}
                        </Text>
                    ))
                ) : (
                    <Text>Nenhum anexo registrado.</Text>
                )}
            </View>
        </Page>
    </Document>
)

export async function downloadOccurrencePdfById(occurrenceId: number): Promise<void> {
    const response = await ApiService.occurrences.findById(occurrenceId)
    const data: Occurrence = response.data
    const providences = [...(data.providences || [])].sort((a, b) => Number(b.id) - Number(a.id))
    const files = (data.FileInOccurrence || []).map(f => ({
        id: f.GeneralFile.id,
        name: f.GeneralFile.title,
    }))
    const blob = await pdf(
        <OccurrencePDFDocument data={data} providences={providences} files={files} />,
    ).toBlob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ocorrencia_${data.id}.pdf`
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

const ViewOccurrenceModal: React.FC = () => {
    const { setModalActive } = useContext(ModalContext)
    const { occurrence } = useContext(OccurrenceContext)
    const { setFeedback } = useContext(FeedbackContext)

    const [fullData, setFullData] = useState<Occurrence | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const loadData = async () => {
            if (!occurrence?.id) {
                setIsLoading(false)
                return
            }

            try {
                const response = await ApiService.occurrences.findById(occurrence.id)
                setFullData(response.data)
            } catch (error) {
                console.error('Erro ao carregar ocorrência:', error)
                setFullData(occurrence)
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [occurrence?.id])

    const data = fullData || occurrence

    const handleFileDownload = async (file: { id: number; name: string }) => {
        if (!data?.id) return
        try {
            const response = await ApiService.occurrences.downloadFile(data.id, file.id)
            const { url } = response.data
            const link = document.createElement('a')
            link.href = url
            link.download = file.name
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Erro ao baixar arquivo:', error)
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: 'Erro ao baixar o arquivo. Tente novamente.',
            })
        }
    }

    if (isLoading) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#8c98a4' }}>
                    Carregando dados da ocorrência...
                </div>
            </Container>
        )
    }

    if (!data) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#8c98a4' }}>
                    Nenhuma ocorrência selecionada.
                </div>
            </Container>
        )
    }

    const providences = [...(data.providences || [])].sort((a, b) => Number(b.id) - Number(a.id))

    const files = (data.FileInOccurrence || []).map(f => ({
        id: f.GeneralFile.id,
        name: f.GeneralFile.title,
    }))

    return (
        <Container>
            <Header>
                <TitleRow>
                    <h2>Ocorrência #{data.id}</h2>
                    <RelevantBadge relevant={data.relevant}>
                        {data.relevant ? 'Relevante' : 'Não relevante'}
                    </RelevantBadge>
                </TitleRow>
                <CloseButton onClick={() => setModalActive(false)}>
                    <X size={16} />
                </CloseButton>
            </Header>

            <InfosContainer>
                {/* Informações Gerais */}
                <Section>
                    <SectionSubtitle>Informações gerais</SectionSubtitle>
                    <InfoGrid>
                        <InfoItem>
                            <InfoLabel>OP</InfoLabel>
                            <InfoValue>{data.operationNumber ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Cliente</InfoLabel>
                            <InfoValue>{data.companyName || data.contactName || 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Setor</InfoLabel>
                            <InfoValue>{data.sector ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Alocação</InfoLabel>
                            <InfoValue>{data.allocation ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Etapa</InfoLabel>
                            <InfoValue>{data.Stage?.name ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Quantidade</InfoLabel>
                            <InfoValue>{data.quantity ?? 'N/A'}</InfoValue>
                        </InfoItem>
                    </InfoGrid>
                </Section>

                <SectionDivider />

                {/* Descrição */}
                <Section>
                    <SectionSubtitle>Descrição</SectionSubtitle>
                    <TextBlock>{data.description || 'Sem descrição.'}</TextBlock>
                </Section>

                <SectionDivider />

                {/* Causa e Correção */}
                <Section>
                    <SectionSubtitle>Causa e Correção</SectionSubtitle>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <TextBlockLabel>Causa</TextBlockLabel>
                            <TextBlock>{data.cause || 'Não informada.'}</TextBlock>
                        </div>
                        <div>
                            <TextBlockLabel>Correção</TextBlockLabel>
                            <TextBlock>{data.correction || 'Não informada.'}</TextBlock>
                        </div>
                    </div>
                </Section>

                <SectionDivider />

                {/* Providências */}
                <Section>
                    <SectionSubtitle>Providências</SectionSubtitle>
                    {providences.length > 0 ? (
                        <SimpleTable>
                            <thead>
                                <tr>
                                    <TableHeader>Descrição</TableHeader>
                                    <TableHeader style={{ width: '180px' }}>Responsável</TableHeader>
                                    <TableHeader style={{ textAlign: 'center', width: '120px' }}>Prazo</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {providences.map((providence, index) => (
                                    <SimpleTableRow key={providence.id || index}>
                                        <TableCell>{providence.description || '-'}</TableCell>
                                        <TableCell>{providence.responsibleName || '-'}</TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            {formatDate(providence.deadline)}
                                        </TableCell>
                                    </SimpleTableRow>
                                ))}
                            </tbody>
                        </SimpleTable>
                    ) : (
                        <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
                            Nenhuma providência registrada.
                        </p>
                    )}
                </Section>

                {/* Anexos */}
                {files.length > 0 && (
                    <>
                        <SectionDivider />
                        <Section>
                            <SectionSubtitle>Anexos</SectionSubtitle>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {files.map(file => (
                                    <div
                                        key={file.id}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 14px',
                                            backgroundColor: '#FAFAFA',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            color: '#262626',
                                        }}
                                    >
                                        <span style={{ flex: 1, wordBreak: 'break-word', marginRight: '12px' }}>
                                            {file.name}
                                        </span>
                                        <span
                                            onClick={() => handleFileDownload(file)}
                                            style={{
                                                color: '#0090d3',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Download
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </InfosContainer>

            <ButtonContainer>
                <PDFDownloadLink
                    document={<OccurrencePDFDocument data={data} providences={providences} files={files} />}
                    fileName={`ocorrencia_${data.id}.pdf`}
                    style={{ textDecoration: 'none' }}
                >
                    {({ loading }) => (
                        <ButtonDiv variant="primary" as="span">
                            <Download size={14} />
                            {loading ? 'Preparando PDF...' : 'Gerar PDF'}
                        </ButtonDiv>
                    )}
                </PDFDownloadLink>
                <ButtonDiv
                    variant="ghost"
                    onClick={() => setModalActive(false)}
                    style={{ color: '#0090d3', fontWeight: 'bold' }}
                >
                    Fechar
                </ButtonDiv>
            </ButtonContainer>
        </Container>
    )
}

export default ViewOccurrenceModal
